/**
 * Offline numbering (M4 task 5): the invoice numbers and KOT numbers a till prints while the
 * cloud is unreachable come from runs reserved for this terminal while it was online.
 *
 * - Invoice blocks (`POST {o}/invoice-blocks`, accounting): consecutive serials of the
 *   outlet's series for the financial year. A serial is taken once and never reused; the
 *   settlement carries it to the server (`payments.settle` `invoiceNo`), which issues the
 *   invoice under it. Serials a till never uses are reported when the block is closed.
 * - KOT ranges (`POST {o}/kot-ranges`, kitchen): numbers of the open business day; the send
 *   carries the one printed (`orders.sendToKitchen` `kotNo`).
 *
 * `topUp` keeps a reserve while online; `takeInvoice` / `takeKot` hand out the next number
 * (null when the reserve ran out: the till then keeps selling and settles when it is back).
 */
import { unwrap } from '@qrkey/api-client';
import { newId } from '@qrkey/contracts/ids';
import { api } from '../app/api.js';
import { db, type LocalBlock, type LocalKotRange } from './db.js';

export const INVOICE_RESERVE_LOW = 10;
export const INVOICE_BLOCK_SIZE = 50;
export const KOT_RESERVE_LOW = 10;
export const KOT_RANGE_SIZE = 100;

const remaining = (r: { fromNo: number; toNo: number; used: number[] }) =>
  r.toNo - r.fromNo + 1 - r.used.length;

export async function invoiceReserve(outletId: string, terminalId: string): Promise<number> {
  const blocks = await db()
    .blocks.where('outletId')
    .equals(outletId)
    .filter((b) => b.terminalId === terminalId && !b.closed)
    .toArray();
  return blocks.reduce((n, b) => n + remaining(b), 0);
}

export async function kotReserve(outletId: string, terminalId: string): Promise<number> {
  const ranges = await db()
    .kotRanges.where('outletId')
    .equals(outletId)
    .filter((r) => r.terminalId === terminalId)
    .toArray();
  return ranges.reduce((n, r) => n + remaining(r), 0);
}

/** Reserve more while online and the reserve is low. Safe to call often and concurrently. */
let topping: Promise<void> | null = null;
export function topUp(outletId: string, terminalId: string): Promise<void> {
  topping ??= doTopUp(outletId, terminalId)
    .catch(() => undefined)
    .finally(() => {
      topping = null;
    });
  return topping;
}

async function doTopUp(outletId: string, terminalId: string): Promise<void> {
  if ((await invoiceReserve(outletId, terminalId)) < INVOICE_RESERVE_LOW) {
    const block = await unwrap(
      api.client.POST('/v1/outlets/{outletId}/invoice-blocks', {
        params: { path: { outletId } },
        body: { id: newId(), terminalId, size: INVOICE_BLOCK_SIZE },
      }),
    );
    await db().blocks.put({
      id: block.id,
      outletId,
      terminalId,
      seriesId: block.seriesId,
      fy: block.fy,
      prefix: block.prefix,
      fromNo: block.fromNo,
      toNo: block.toNo,
      used: [],
      closed: false,
      reservedAt: block.reservedAt,
    } satisfies LocalBlock);
  }
  if ((await kotReserve(outletId, terminalId)) < KOT_RESERVE_LOW) {
    const range = await unwrap(
      api.client.POST('/v1/outlets/{outletId}/kot-ranges', {
        params: { path: { outletId } },
        body: { id: newId(), terminalId, size: KOT_RANGE_SIZE },
      }),
    );
    await db().kotRanges.put({
      id: range.id,
      outletId,
      terminalId,
      businessDate: range.businessDate,
      fromNo: range.fromNo,
      toNo: range.toNo,
      used: [],
    } satisfies LocalKotRange);
  }
}

export interface TakenInvoice {
  serial: number;
  prefix: string;
  fy: string;
  blockId: string;
}

/** The next unused serial of this terminal's oldest open block, marked used. */
export async function takeInvoice(
  outletId: string,
  terminalId: string,
): Promise<TakenInvoice | null> {
  return db().transaction('rw', db().blocks, async () => {
    const blocks = (
      await db()
        .blocks.where('outletId')
        .equals(outletId)
        .filter((b) => b.terminalId === terminalId && !b.closed)
        .toArray()
    ).sort((a, b) => a.fromNo - b.fromNo);
    for (const b of blocks) {
      for (let n = b.fromNo; n <= b.toNo; n++) {
        if (b.used.includes(n)) continue;
        await db().blocks.update(b.id, { used: [...b.used, n] });
        return { serial: n, prefix: b.prefix, fy: b.fy, blockId: b.id };
      }
    }
    return null;
  });
}

export interface TakenKot {
  kotNo: number;
  rangeId: string;
}

/** The next unused KOT number of this terminal's ranges (today's first), marked used. */
export async function takeKot(
  outletId: string,
  terminalId: string,
  businessDate: string,
): Promise<TakenKot | null> {
  return db().transaction('rw', db().kotRanges, async () => {
    const ranges = (
      await db()
        .kotRanges.where('outletId')
        .equals(outletId)
        .filter((r) => r.terminalId === terminalId)
        .toArray()
    ).sort(
      (a, b) =>
        Number(b.businessDate === businessDate) - Number(a.businessDate === businessDate) ||
        b.businessDate.localeCompare(a.businessDate) ||
        a.fromNo - b.fromNo,
    );
    for (const r of ranges) {
      for (let n = r.fromNo; n <= r.toNo; n++) {
        if (r.used.includes(n)) continue;
        await db().kotRanges.update(r.id, { used: [...r.used, n] });
        return { kotNo: n, rangeId: r.id };
      }
    }
    return null;
  });
}

/** Give a serial back when the command that took it was refused before anything was printed. */
export async function releaseInvoice(blockId: string, serial: number): Promise<void> {
  const b = await db().blocks.get(blockId);
  if (b) await db().blocks.update(blockId, { used: b.used.filter((n) => n !== serial) });
}
